import React, { useState, useEffect } from 'react';
import { Flex, TextV2, useToast } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '../../../JsonComponent.types';
import { AddCircle, WarningTriangle } from '@withjoy/joykit/icons';
import { useGoToReorderView } from '../../../hooks/useJsonComponentNavigation';
import { useSaveJsonData } from '../../../hooks/useJsonBlobStorageUtils';
import { FaqConfig } from '../Faq.config';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useTranslation } from '@shared/core';
import { FaqData, FaqItemWithId } from '../Faq.types';
import { BottomBar } from '../../../shared/BottomBar';
import { ProgressiveBlur } from '@shared/components/ProgressiveBlur/ProgressiveBlur';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { FaqList } from '../components/FaqList';
import { FaqForm } from '../components/FaqForm';

type ViewMode = 'list' | 'edit' | 'add';

export const FaqEdit = (props: AdminSidebarComponentProps) => {
  const { tq } = useTranslation('faqCustomComponent');
  const { goToReorderView } = useGoToReorderView();
  const { saveJsonData } = useSaveJsonData();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setRefetchQueries } = useCustomComponentStateContext();
  const { toast } = useToast();

  const faqContent: FaqData = props.componentData.containerContent.content || { items: [] };

  // Add IDs to items for drag and drop
  const [items, setItems] = useState<FaqItemWithId[]>(() =>
    faqContent.items.map((item, index) => ({
      ...item,
      id: `faq-${index}-${Date.now()}`,
    })),
  );

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingItem, setEditingItem] = useState<FaqItemWithId | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSubtitle, setEditSubtitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track if there are unsaved changes
  useEffect(() => {
    if (viewMode === 'list') {
      setHasUnsavedChanges(false);
      return;
    }

    if (viewMode === 'add') {
      // In add mode, any non-empty content is considered unsaved
      const hasChanges = editTitle.trim() !== '' || editSubtitle.trim() !== '';
      setHasUnsavedChanges(hasChanges);
    } else if (viewMode === 'edit' && editingItem) {
      // In edit mode, compare against original values
      const hasChanges = editTitle !== editingItem.title || editSubtitle !== editingItem.subtitle;
      setHasUnsavedChanges(hasChanges);
    }
  }, [editTitle, editSubtitle, viewMode, editingItem]);

  const handleAddNew = () => {
    setEditTitle('');
    setEditSubtitle('');
    setEditingItem(null);
    setViewMode('add');
  };

  const handleEdit = (item: FaqItemWithId) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditSubtitle(item.subtitle);
    setViewMode('edit');
  };

  const handleDelete = (item: FaqItemWithId) => {
    const newItems = items.filter(i => i.id !== item.id);
    setItems(newItems);
    handleSave(newItems);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editSubtitle.trim()) {
      toast(tq`Title and subtitle are required`, { icon: <WarningTriangle />, duration: 3000 });
      return;
    }

    let newItems: FaqItemWithId[];

    if (viewMode === 'add') {
      // Adding new item
      newItems = [
        ...items,
        {
          id: `faq-${items.length}-${Date.now()}`,
          title: editTitle,
          subtitle: editSubtitle,
        },
      ];
    } else {
      // Editing existing item
      newItems = items.map(item => (item.id === editingItem?.id ? { ...item, title: editTitle, subtitle: editSubtitle } : item));
    }

    setItems(newItems);
    await handleSave(newItems);
    setViewMode('list');
  };

  const handleSave = async (itemsToSave: FaqItemWithId[]) => {
    setLoading(true);
    try {
      // Remove IDs before saving - schema has items array with title and subtitle
      const dataToSave: FaqData = {
        items: itemsToSave.map(({ title, subtitle }) => ({ title, subtitle })),
      };

      await saveJsonData({
        id: props.componentData.containerContent.id,
        content: dataToSave,
        type: FaqConfig.componentType,
        schemaUrl: FaqConfig.schemaUrl,
      });

      setRefetchQueries(['PageContainerBySlug']);
      updateWebsitePreview();
    } catch (error) {
      toast(tq`Failed to save FAQ`, { icon: <WarningTriangle />, duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = (reorderedItems: FaqItemWithId[]) => {
    setItems(reorderedItems);
    handleSave(reorderedItems);
  };

  const handleBackClicked = () => {
    if (hasUnsavedChanges) {
      setShowConfirmation(true);
    } else if (viewMode === 'list') {
      goToReorderView();
    } else {
      setViewMode('list');
    }
  };

  const handleConfirmExit = () => {
    setShowConfirmation(false);
    setEditTitle('');
    setEditSubtitle('');
    setViewMode('list');
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'list':
        return (
          <FaqList
            items={items}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
        );
      case 'edit':
      case 'add':
        return (
          <FaqForm
            title={editTitle}
            subtitle={editSubtitle}
            onTitleChange={setEditTitle}
            onSubtitleChange={setEditSubtitle}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Flex
        flexDirection="column"
        padding="1rem"
      >
        <Flex
          justifyContent="space-between"
          marginBottom="1rem"
          paddingX="0.5rem"
        >
          <TextV2 typographyVariant="hed6">{tq`FAQs`}</TextV2>
        </Flex>

        {renderContent()}

        <BottomBar
          actions={[
            {
              label: tq`Back`,
              variant: 'primary',
              onClick: handleBackClicked,
            },
            {
              label: tq`Add`,
              variant: 'secondary',
              onClick: handleAddNew,
              hidden: viewMode !== 'list',
              buttonProps: {
                startIcon: <AddCircle />,
              },
            },
            {
              label: viewMode === 'add' ? tq`Save` : tq`Update`,
              variant: 'secondary',
              onClick: handleSaveEdit,
              hidden: viewMode === 'list',
              buttonProps: {
                loading,
              },
            },
          ]}
        >
          <ProgressiveBlur containerProps={{ right: '16px' }} />
        </BottomBar>
      </Flex>

      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmExit}
        title={tq`Exit without saving?`}
        message={tq`Are you sure you want to exit without saving? All unsaved changes will be lost.`}
        confirmText={tq`Exit`}
        cancelText={tq`Cancel`}
      />
    </>
  );
};
